import { FormEvent, useEffect, useState } from 'react';
import { api, getToken } from '../../api/client';
import { t } from '../../hooks/useT';

interface ZoomLevel {
  level: number;
  imageUrl: string;
}

interface AdminPuzzle {
  id: string;
  slug: string;
  puzzleNumber: number;
  publishDate: string | null;
  status: string;
  pool: string;
  answerCanonical: string;
  answerEn?: string | null;
  searchQuery?: string | null;
  photoProvider?: string | null;
  adminReviewed: boolean;
  aliases: string[];
  zoomLevels: ZoomLevel[];
}

type PhotoProviderChoice = 'auto' | 'pexels' | 'unsplash';

function PuzzleCard({
  puzzle,
  onSaved,
}: {
  puzzle: AdminPuzzle;
  onSaved: (p: AdminPuzzle) => void;
}) {
  const [answer, setAnswer] = useState(puzzle.answerCanonical);
  const [answerEn, setAnswerEn] = useState(puzzle.answerEn || '');
  const [searchQuery, setSearchQuery] = useState(puzzle.searchQuery || '');
  const [provider, setProvider] = useState<PhotoProviderChoice>('auto');
  const [aliases, setAliases] = useState(puzzle.aliases.join(', '));
  const [level, setLevel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setAnswer(puzzle.answerCanonical);
    setAnswerEn(puzzle.answerEn || '');
    setSearchQuery(puzzle.searchQuery || '');
    setAliases(puzzle.aliases.join(', '));
    setLevel(0);
  }, [
    puzzle.id,
    puzzle.answerCanonical,
    puzzle.answerEn,
    puzzle.searchQuery,
    puzzle.aliases,
    puzzle.adminReviewed,
  ]);

  const imageUrl = puzzle.zoomLevels.find((z) => z.level === level)?.imageUrl
    || puzzle.zoomLevels[0]?.imageUrl;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const res = await api<{ puzzle: AdminPuzzle }>(`/admin/puzzles/${puzzle.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          answer,
          aliases,
          searchQuery,
          answerEn,
        }),
      });
      onSaved(res.puzzle);
      setMsg(t('admin.puzzles.saved'));
    } catch {
      setMsg(t('admin.puzzles.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const handleRefetchPhoto = async () => {
    setImageBusy(true);
    setMsg('');
    try {
      const res = await api<{ puzzle: AdminPuzzle }>(`/admin/puzzles/${puzzle.id}/refetch-photo`, {
        method: 'POST',
        body: JSON.stringify({
          answer: answer.trim() || puzzle.answerCanonical,
          searchQuery: searchQuery.trim() || undefined,
          provider,
        }),
      });
      onSaved(res.puzzle);
      setMsg(t('admin.puzzles.imageReplaced'));
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setMsg(
        err.data?.error === 'stock_photo_unavailable'
          ? t('admin.puzzles.stockUnavailable')
          : t('admin.puzzles.imageError'),
      );
    } finally {
      setImageBusy(false);
    }
  };

  return (
    <article className={`admin-puzzle-card${puzzle.adminReviewed ? ' admin-puzzle-card--reviewed' : ''}`}>
      <header className="admin-puzzle-head">
        <span className="admin-puzzle-num">#{puzzle.puzzleNumber}</span>
        <span className="admin-badge">{puzzle.pool}</span>
        <span className="admin-badge admin-badge-muted">{puzzle.status}</span>
        {puzzle.adminReviewed && (
          <span className="admin-badge admin-badge-reviewed">{t('admin.puzzles.reviewed')}</span>
        )}
        {puzzle.photoProvider && (
          <span className="admin-badge admin-badge-provider">{puzzle.photoProvider}</span>
        )}
      </header>

      {imageUrl && (
        <div className="admin-puzzle-preview">
          <img src={imageUrl} alt="" />
        </div>
      )}

      {puzzle.zoomLevels.length > 1 && (
        <div className="admin-zoom-levels">
          {puzzle.zoomLevels.map((z) => (
            <button
              key={z.level}
              type="button"
              className={`admin-zoom-btn${level === z.level ? ' active' : ''}`}
              onClick={() => setLevel(z.level)}
            >
              {t('admin.puzzles.level')} {z.level}
            </button>
          ))}
        </div>
      )}

      <section className="admin-puzzle-section">
        <h3 className="admin-puzzle-section-title">{t('admin.puzzles.sectionPhoto')}</h3>
        <div className="admin-puzzle-refetch">
          <label className="admin-puzzle-field">
            <span>{t('admin.puzzles.searchQuery')}</span>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.puzzles.searchQueryPlaceholder')}
            />
          </label>
          <div className="admin-puzzle-refetch-actions">
            <label className="admin-puzzle-field">
              <span>{t('admin.puzzles.photoProvider')}</span>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as PhotoProviderChoice)}
                aria-label={t('admin.puzzles.photoProvider')}
              >
                <option value="auto">{t('admin.puzzles.providerAuto')}</option>
                <option value="pexels">{t('admin.puzzles.providerPexels')}</option>
                <option value="unsplash">{t('admin.puzzles.providerUnsplash')}</option>
              </select>
            </label>
            <button
              type="button"
              className="btn secondary admin-btn-sm admin-puzzle-refetch-btn"
              disabled={imageBusy || !answer.trim()}
              onClick={() => void handleRefetchPhoto()}
            >
              {imageBusy ? t('common.loading') : t('admin.puzzles.replaceImage')}
            </button>
          </div>
        </div>
      </section>

      <section className="admin-puzzle-section">
        <h3 className="admin-puzzle-section-title">{t('admin.puzzles.sectionAnswers')}</h3>
        <form className="admin-form admin-form-inline" onSubmit={handleSave}>
          <label>
            {t('admin.puzzles.answer')}
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} required />
          </label>
          <label>
            {t('admin.puzzles.answerEn')}
            <input value={answerEn} onChange={(e) => setAnswerEn(e.target.value)} />
          </label>
          <label>
            {t('admin.puzzles.aliases')}
            <input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </label>
          <button type="submit" className="btn primary admin-puzzle-save" disabled={busy}>
            {t('admin.puzzles.save')}
          </button>
        </form>
      </section>
      {msg && <p className="admin-msg">{msg}</p>}
    </article>
  );
}

export function AdminPuzzlesTab() {
  const [puzzles, setPuzzles] = useState<AdminPuzzle[]>([]);
  const [pool, setPool] = useState('');
  const [status, setStatus] = useState('');
  const [reviewed, setReviewed] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [batchMsg, setBatchMsg] = useState('');
  const [batchBusy, setBatchBusy] = useState<'daily' | 'premium' | null>(null);
  const [answer, setAnswer] = useState('');
  const [answerEn, setAnswerEn] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [aliases, setAliases] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (pool) params.set('pool', pool);
    if (status) params.set('status', status);
    if (reviewed) params.set('reviewed', reviewed);
    const qs = params.toString();
    api<{ puzzles: AdminPuzzle[] }>(`/admin/puzzles${qs ? `?${qs}` : ''}`)
      .then((res) => setPuzzles(res.puzzles))
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [pool, status, reviewed]);

  const runBatch = async (batchPool: 'daily' | 'premium') => {
    setBatchBusy(batchPool);
    setBatchMsg('');
    try {
      const res = await api<{ imported: number; remaining?: number }>('/admin/puzzles/import-batch', {
        method: 'POST',
        body: JSON.stringify({ pool: batchPool }),
      });
      setBatchMsg(t('admin.puzzles.batchDone').replace('{n}', String(res.imported)));
      load();
    } catch (e: unknown) {
      const err = e as { data?: { error?: string; bankSize?: number; inDatabase?: number } };
      setBatchMsg(
        err.data?.error === 'bank_exhausted'
          ? t('admin.puzzles.batchExhausted')
              .replace('{bankSize}', String(err.data.bankSize ?? '?'))
              .replace('{inDatabase}', String(err.data.inDatabase ?? '?'))
          : t('common.error'),
      );
    } finally {
      setBatchBusy(null);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    fd.append('answer', answer);
    fd.append('aliases', aliases);
    if (searchQuery.trim()) fd.append('searchQuery', searchQuery.trim());
    if (answerEn.trim()) fd.append('answerEn', answerEn.trim());
    if (publishDate) fd.append('publishDate', publishDate);

    const token = getToken();
    const res = await fetch('/api/admin/puzzles', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) {
      setCreateMsg(data.error || t('common.error'));
      return;
    }
    setCreateMsg(`${t('admin.puzzles.created')} ${data.slug}`);
    setShowCreate(false);
    load();
  };

  const handleSaved = (updated: AdminPuzzle) => {
    setPuzzles((prev) => {
      if (reviewed === 'false' && updated.adminReviewed) {
        return prev.filter((p) => p.id !== updated.id);
      }
      if (reviewed === 'true' && !updated.adminReviewed) {
        return prev.filter((p) => p.id !== updated.id);
      }
      return prev.map((p) => (p.id === updated.id ? updated : p));
    });
  };

  return (
    <div className="admin-puzzles">
      <div className="admin-batch-bar">
        <div className="admin-batch-group">
          <span className="admin-batch-label">{t('admin.puzzles.poolDaily')}</span>
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            disabled={batchBusy !== null}
            onClick={() => runBatch('daily')}
          >
            {batchBusy === 'daily' ? t('common.loading') : t('admin.puzzles.batchDaily')}
          </button>
        </div>
        <div className="admin-batch-group">
          <span className="admin-batch-label">{t('admin.puzzles.poolPremium')}</span>
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            disabled={batchBusy !== null}
            onClick={() => runBatch('premium')}
          >
            {batchBusy === 'premium' ? t('common.loading') : t('admin.puzzles.batchPremium')}
          </button>
        </div>
      </div>
      {batchMsg && <p className="admin-msg">{batchMsg}</p>}

      <div className="admin-filters">
        <select value={pool} onChange={(e) => setPool(e.target.value)} aria-label={t('admin.puzzles.poolAll')}>
          <option value="">{t('admin.puzzles.poolAll')}</option>
          <option value="daily">{t('admin.puzzles.poolDaily')}</option>
          <option value="premium">{t('admin.puzzles.poolPremium')}</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('admin.puzzles.statusAll')}>
          <option value="">{t('admin.puzzles.statusAll')}</option>
          <option value="draft">draft</option>
          <option value="scheduled">scheduled</option>
          <option value="published">published</option>
          <option value="active">active</option>
        </select>
        <select value={reviewed} onChange={(e) => setReviewed(e.target.value)} aria-label={t('admin.puzzles.reviewedAll')}>
          <option value="">{t('admin.puzzles.reviewedAll')}</option>
          <option value="false">{t('admin.puzzles.reviewedPending')}</option>
          <option value="true">{t('admin.puzzles.reviewedDone')}</option>
        </select>
        <button type="button" className="btn secondary" onClick={() => setShowCreate((v) => !v)}>
          {t('admin.puzzles.create')}
        </button>
      </div>

      {showCreate && (
        <form className="admin-form admin-create-form" onSubmit={handleCreate}>
          <label className="admin-create-field--full">
            {t('admin.puzzles.image')}
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
          </label>
          <div className="admin-form-row admin-form-row--2">
            <label>
              {t('admin.puzzles.answer')}
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} required />
            </label>
            <label>
              {t('admin.puzzles.answerEn')}
              <input value={answerEn} onChange={(e) => setAnswerEn(e.target.value)} />
            </label>
          </div>
          <label>
            {t('admin.puzzles.searchQuery')}
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.puzzles.searchQueryPlaceholder')}
            />
          </label>
          <label>
            {t('admin.puzzles.aliases')}
            <input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </label>
          <label>
            {t('admin.puzzles.publishDate')}
            <input type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} />
          </label>
          <button type="submit" className="btn primary admin-create-field--full">{t('admin.puzzles.create')}</button>
        </form>
      )}
      {createMsg && <p className="admin-msg">{createMsg}</p>}

      {loading && <p className="admin-msg">{t('common.loading')}</p>}
      {error && <p className="admin-msg">{error}</p>}

      {!loading && !error && puzzles.length === 0 && (
        <p className="admin-msg">{t('admin.puzzles.empty')}</p>
      )}

      <div className="admin-puzzle-grid">
        {puzzles.map((puzzle) => (
          <PuzzleCard key={puzzle.id} puzzle={puzzle} onSaved={handleSaved} />
        ))}
      </div>
    </div>
  );
}
